import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getAuthHeaders } from '../utils/auth';
import { API_ENDPOINTS } from '../config/api';
import './ReferralHistory.css';

export default function ReferralHistory() {
  const navigate = useNavigate();
  const [referralCode, setReferralCode] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) {
      const timeout = setTimeout(() => {
        if (!isAuthenticated()) {
          navigate('/signup');
        }
      }, 100);
      return () => clearTimeout(timeout);
    }
  }, [navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch referral code and history in parallel
        const [codeRes, historyRes] = await Promise.all([
          fetch(API_ENDPOINTS.REFERRALS.MY_CODE, {
            method: 'GET',
            credentials: 'include',
            headers: getAuthHeaders(),
          }),
          fetch(API_ENDPOINTS.REFERRALS.HISTORY, {
            method: 'GET',
            credentials: 'include',
            headers: getAuthHeaders(),
          }),
        ]);

        if (codeRes.ok) {
          const codeData = await codeRes.json();
          setReferralCode(codeData.referralCode || '');
        }

        if (historyRes.ok) {
          const historyData = await historyRes.json();
          setReferrals(Array.isArray(historyData.referrals) ? historyData.referrals : []);
        }
      } catch (e) {
        console.error('Failed to fetch referral data:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const referralLink = referralCode
    ? `${window.location.origin}/buy-credits?ref=${referralCode}`
    : '';

  const handleCopyReferral = useCallback(async () => {
    if (!referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = referralLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [referralLink]);

  return (
    <div className="referral-history-page">
      <div className="referral-history-container">
        <div className="referral-history-header">
          <h1>Referral Program</h1>
        </div>

        {/* Referral Code Card */}
        {referralCode && (
          <div className="referral-code-card">
            <h2>Your Referral Code</h2>
            <div className="referral-code-display">
              <span className="referral-code-value">{referralCode}</span>
              <button className="btn-copy-ref" onClick={handleCopyReferral}>
                {copied ? '✓ Copied!' : '📋 Copy Link'}
              </button>
              <a
                href={`https://wa.me/?text=${encodeURIComponent('Try BlackKite AI for interview prep! Use my referral link:')}%20${encodeURIComponent(referralLink)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="share-btn share-whatsapp"
                title="Share on WhatsApp"
              >
                WhatsApp
              </a>
            </div>
            <p className="referral-link-preview">{referralLink}</p>
            <p className="referral-description">
              🎁 Refer a friend and earn <strong>1 free credit</strong> when they successfully make a purchase!
            </p>
          </div>
        )}

        {/* Referral History Table */}
        <div className="referral-table-wrapper">
          <h2 className="referral-table-title">Referral History</h2>
          <table className="referral-table">
            <thead>
              <tr>
                <th>Referred User</th>
                <th>Status</th>
                <th>Credits Earned</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="4" className="empty-state">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', padding: '40px' }}>
                      <div className="spinner" style={{
                        border: '3px solid rgba(255, 255, 255, 0.1)',
                        borderTop: '3px solid #10b981',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span style={{ color: '#9ca3af' }}>Loading referral history...</span>
                    </div>
                  </td>
                </tr>
              ) : referrals.length === 0 ? (
                <tr>
                  <td colSpan="4" className="empty-state">
                    No referrals yet. Share your code to start earning rewards!
                  </td>
                </tr>
              ) : (
                referrals.map((r, idx) => (
                  <tr key={r.id || idx}>
                    <td>{r.referredUserEmail || r.referredUserName || r.referredEmail || r.referredUser || '—'}</td>
                    <td>
                      <span className={`status-badge status-${(r.status || 'pending').toLowerCase()}`}>
                        {r.status || 'Pending'}
                      </span>
                    </td>
                    <td>{r.creditsAwarded || r.reward || r.rewardCredits || '—'}</td>
                    <td>{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
